import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchProfile } from '../store/slices/profileSlice';
import { profileApi, ProfileUpdateData } from '../services/profileApi';

const banks = [
  'Al Baraka Bank (Pakistan) Limited',
  'Allied Bank Limited',
  'Askari Islamic Bank',
  'Bank Alfalah Limited',
  'Bank Al-Habib Limited',
  'BankIslami Pakistan Limited',
  'Citi Bank',
  'Deutsche Bank A.G.',
  'The Bank of Tokyo-Mitsubishi UFJ',
  'Dubai Islamic Bank Pakistan Limited',
  'Faysal Bank Limited',
  'Jazz Cash Bank Limited',
  'First Women Bank Limited',
  'Habib Bank Limited',
  'Standard Chartered Bank (Pakistan) Limited',
  'Habib Metropolitan Bank Limited',
  'Industrial and Commercial Bank of China',
  'Industrial Development Bank of Pakistan',
  'JS Bank Limited',
  'MCB Bank Limited',
  'MCB Islamic Bank Limited',
  'Meezan Bank Limited',
  'National Bank of Pakistan',
  'Bank of Punjab',
  'Sindh Bank',
  'Bank of Khyber',
  'Zarai Taraqiyati Bank Limited',
  'Bank of Azad Jammu & Kashmir'
];

interface EditFormData {
  // User fields
  name: string;
  // Client profile fields
  company_name: string;
  address: string;
  web_address: string;
  brand_name: string;
  business_sector: string;
  products: string;
  authorized_person_name: string;
  authorized_designation: string;
  authorized_phone: string;
  authorized_cnic: string;
  admin_contact_name: string;
  admin_phone: string;
  finance_contact_name: string;
  ntn: string;
  gst: string;
  pickup_address: string;
  pickup_contact_person: string;
  pickup_phone: string;
  pickup_email: string;
  invoicing: string;
  // Bank account fields
  bank_account_title: string;
  bank_name: string;
  bank_iban: string;
  bank_account_number: string;
}

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { user, loading, error } = useAppSelector((state) => state.profile);
  const { user: authUser } = useAppSelector((state) => state.auth);

  // Check if user is a client
  const isClient = authUser?.type === 'CLIENT';

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Snackbar state for success toast
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState<EditFormData>({
    name: '',
    company_name: '',
    address: '',
    web_address: '',
    brand_name: '',
    business_sector: '',
    products: '',
    authorized_person_name: '',
    authorized_designation: '',
    authorized_phone: '',
    authorized_cnic: '',
    admin_contact_name: '',
    admin_phone: '',
    finance_contact_name: '',
    ntn: '',
    gst: '',
    pickup_address: '',
    pickup_contact_person: '',
    pickup_phone: '',
    pickup_email: '',
    invoicing: '',
    bank_account_title: '',
    bank_name: '',
    bank_iban: '',
    bank_account_number: '',
  });

  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      const cp = user.clientProfile;
      // Get primary bank account if exists
      const primaryBank = user.bankAccounts?.find(ba => ba.primary) || user.bankAccounts?.[0];
      setFormData({
        name: user.name || '',
        company_name: cp?.company_name || '',
        address: cp?.address || '',
        web_address: cp?.web_address || '',
        brand_name: cp?.brand_name || '',
        business_sector: cp?.business_sector || '',
        products: cp?.products || '',
        authorized_person_name: cp?.authorized_person_name || '',
        authorized_designation: cp?.authorized_designation || '',
        authorized_phone: cp?.authorized_phone || '',
        authorized_cnic: cp?.authorized_cnic || '',
        admin_contact_name: cp?.admin_contact_name || '',
        admin_phone: cp?.admin_phone || '',
        finance_contact_name: cp?.finance_contact_name || '',
        ntn: cp?.ntn || '',
        gst: cp?.gst || '',
        pickup_address: cp?.pickup_address || '',
        pickup_contact_person: cp?.pickup_contact_person || '',
        pickup_phone: cp?.pickup_phone || '',
        pickup_email: cp?.pickup_email || '',
        invoicing: cp?.invoicing || '',
        bank_account_title: primaryBank?.title || '',
        bank_name: primaryBank?.bank_name || '',
        bank_iban: primaryBank?.iban || '',
        bank_account_number: primaryBank?.number || '',
      });
    }
  }, [user]);

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  const handleTermsAndConditions = () => {
    window.open('/terms-and-conditions', '_blank');
  };

  const handleOpenEditModal = () => {
    setEditError(null);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditError(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    setEditLoading(true);
    setEditError(null);

    try {
      await profileApi.updateProfile(formData as ProfileUpdateData);
      // Close modal immediately on success
      handleCloseEditModal();
      // Show success toast
      setSnackbarMessage('Profile updated successfully!');
      setSnackbarOpen(true);
      // Refresh profile data
      dispatch(fetchProfile());
    } catch (err: any) {
      setEditError(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <Alert severity="warning">
          No profile data found.
        </Alert>
      </Box>
    );
  }

  const { clientProfile, bankAccounts } = user;

  // Common card styles for consistent height and scrollable content
  const cardStyles = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const cardContentStyles = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    p: 3,
  };

  const tableContainerStyles = {
    flex: 1,
    maxHeight: '400px',
    overflow: 'auto',
  };

  return (
    <Box maxWidth={1200} mx="auto" mt={4} px={2}>
      {/* Header with title and action buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4">My Profile</Typography>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          {isClient && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleOpenEditModal}
              startIcon={<EditIcon />}
              size="medium"
            >
              Edit Profile
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
            size="medium"
          >
            Change Password
          </Button>
          <Button
            variant="outlined"
            color="info"
            onClick={handleTermsAndConditions}
            size="medium"
          >
            Terms & Conditions
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: isClient ? 6 : 12 }}>
          <Card sx={cardStyles}>
            <CardContent sx={cardContentStyles}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <TableContainer sx={tableContainerStyles}>
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>Name</TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Login ID</TableCell>
                      <TableCell>{user.loginID || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Role</TableCell>
                      <TableCell>
                        <Chip
                          label={authUser?.type || 'User'}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell>
                        <Chip
                          label={user.status === 1 ? 'Active' : 'Pending'}
                          color={user.status === 1 ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Registration Date</TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                    {user.branch && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Associated Branch</TableCell>
                        <TableCell>{user.branch.name}</TableCell>
                      </TableRow>
                    )}
                    {isClient && (
                      <>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Account No</TableCell>
                          <TableCell>{clientProfile?.account_no || '-'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Area/Zone</TableCell>
                          <TableCell>
                            {clientProfile?.areaZone ? `${clientProfile.areaZone.code} - ${clientProfile.areaZone.name}` : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Point Of Sale</TableCell>
                          <TableCell>
                            {clientProfile?.pointOfSale ? `${clientProfile.pointOfSale.code} - ${clientProfile.pointOfSale.name}` : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Account Type</TableCell>
                          <TableCell>{clientProfile?.account_type || '-'}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Business Information - Client only */}
        {isClient && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={cardStyles}>
            <CardContent sx={cardContentStyles}>
              <Typography variant="h6" gutterBottom>Business Information</Typography>
              <TableContainer sx={tableContainerStyles}>
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>BRS</TableCell>
                      <TableCell>{clientProfile?.business_registration_status || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                      <TableCell>{clientProfile?.company_name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Address</TableCell>
                      <TableCell>{clientProfile?.address || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Web Address</TableCell>
                      <TableCell>
                        {clientProfile?.web_address ? (
                          <a href={clientProfile.web_address} target="_blank" rel="noopener noreferrer">
                            {clientProfile.web_address}
                          </a>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Brand Name</TableCell>
                      <TableCell>{clientProfile?.brand_name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Business Sector</TableCell>
                      <TableCell>{clientProfile?.business_sector || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Products</TableCell>
                      <TableCell>{clientProfile?.products || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Contact Information - Client only */}
        {isClient && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={cardStyles}>
            <CardContent sx={cardContentStyles}>
              <Typography variant="h6" gutterBottom>Contact Information</Typography>
              <TableContainer sx={tableContainerStyles}>
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>Authorized Person</TableCell>
                      <TableCell>{clientProfile?.authorized_person_name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Designation</TableCell>
                      <TableCell>{clientProfile?.authorized_designation || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Phone Number</TableCell>
                      <TableCell>{clientProfile?.authorized_phone || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>CNIC</TableCell>
                      <TableCell>{clientProfile?.authorized_cnic || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Admin Name</TableCell>
                      <TableCell>{clientProfile?.admin_contact_name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Admin Phone Number</TableCell>
                      <TableCell>{clientProfile?.admin_phone || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Bank Information - Client only */}
        {isClient && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={cardStyles}>
            <CardContent sx={cardContentStyles}>
              <Typography variant="h6" gutterBottom>Bank Information</Typography>
              <TableContainer sx={{ ...tableContainerStyles, maxHeight: '200px' }}>
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>Finance Contact Name</TableCell>
                      <TableCell>{clientProfile?.finance_contact_name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>NTN</TableCell>
                      <TableCell>{clientProfile?.ntn || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>GST</TableCell>
                      <TableCell>{clientProfile?.gst || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Bank Accounts</Typography>
              <TableContainer sx={{ ...tableContainerStyles, maxHeight: '200px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Bank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Account No.</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>IBAN</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Primary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bankAccounts && bankAccounts.length > 0 ? (
                      bankAccounts.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.title || '-'}</TableCell>
                          <TableCell>{row.bank_name || '-'}</TableCell>
                          <TableCell>{row.number || '-'}</TableCell>
                          <TableCell>{row.iban || '-'}</TableCell>
                          <TableCell>
                            {row.primary ? <Chip label="Primary" color="success" size="small" /> : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No bank accounts found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Pickup Information - Client only */}
        {isClient && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={cardStyles}>
            <CardContent sx={cardContentStyles}>
              <Typography variant="h6" gutterBottom>Pickup Information</Typography>
              <TableContainer sx={tableContainerStyles}>
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>Pickup Address</TableCell>
                      <TableCell>{clientProfile?.pickup_address || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Contact Person</TableCell>
                      <TableCell>{clientProfile?.pickup_contact_person || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Phone Number</TableCell>
                      <TableCell>{clientProfile?.pickup_phone || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Email Address</TableCell>
                      <TableCell>{clientProfile?.pickup_email || '-'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      {/* Edit Profile Modal - Client only */}
      {isClient && (
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Profile
          <IconButton onClick={handleCloseEditModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}

          {/* User Information Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, mt: 1 }}>
            User Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Business Information Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Business Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Brand Name"
                name="brand_name"
                value={formData.brand_name}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                size="small"
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Web Address"
                name="web_address"
                value={formData.web_address}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Business Sector"
                name="business_sector"
                value={formData.business_sector}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                size="small"
                label="Products"
                name="products"
                value={formData.products}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Contact Information Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Contact Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Authorized Person Name"
                name="authorized_person_name"
                value={formData.authorized_person_name}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Designation"
                name="authorized_designation"
                value={formData.authorized_designation}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Authorized Person Phone"
                name="authorized_phone"
                value={formData.authorized_phone}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="CNIC"
                name="authorized_cnic"
                value={formData.authorized_cnic}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Admin Contact Name"
                name="admin_contact_name"
                value={formData.admin_contact_name}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Admin Phone"
                name="admin_phone"
                value={formData.admin_phone}
                onChange={handleInputChange}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Finance Information Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Finance Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
              <TextField
                fullWidth
                size="small"
                label="Finance Contact Name"
                name="finance_contact_name"
                value={formData.finance_contact_name}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
              <FormControl fullWidth size="small">
                <InputLabel id="invoicing-label">Invoicing</InputLabel>
                <Select
                  labelId="invoicing-label"
                  name="invoicing"
                  value={formData.invoicing}
                  label="Invoicing"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
              <TextField
                fullWidth
                size="small"
                label="NTN"
                name="ntn"
                value={formData.ntn}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
              <TextField
                fullWidth
                size="small"
                label="GST"
                name="gst"
                value={formData.gst}
                onChange={handleInputChange}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Bank Account Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bank Account Details
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Account Title"
                name="bank_account_title"
                value={formData.bank_account_title}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <FormControl fullWidth size="small">
                <InputLabel id="bank-name-label">Bank Name</InputLabel>
                <Select
                  labelId="bank-name-label"
                  name="bank_name"
                  value={formData.bank_name}
                  label="Bank Name"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">Select Bank</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="IBAN"
                name="bank_iban"
                value={formData.bank_iban}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Account Number"
                name="bank_account_number"
                value={formData.bank_account_number}
                onChange={handleInputChange}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Pickup Information Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Pickup Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                size="small"
                label="Pickup Address"
                name="pickup_address"
                value={formData.pickup_address}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Pickup Contact Person"
                name="pickup_contact_person"
                value={formData.pickup_contact_person}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Pickup Phone"
                name="pickup_phone"
                value={formData.pickup_phone}
                onChange={handleInputChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                size="small"
                label="Pickup Email"
                name="pickup_email"
                type="email"
                value={formData.pickup_email}
                onChange={handleInputChange}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            color="primary"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
